import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FilmPlayer, FILM_LENGTH, CHAPTERS, filmEnabled } from '../components/FilmLightbox';
import { PageFilm, pageFilmsEnabled } from '../components/PageFilm';
import { moduleBySlug } from '../data/modules';
import { appKit, platformStart } from '../data/plans';
import PageWorld from '../components/site/PageWorld';
import { WATCH } from '../data/worlds';

// /watch — the bridge film on its own page. Seven chapters: the bridge,
// the platform (nQube as the specification, the swarm, models, knowledge),
// govern (the hold, then the same gate on a branch, a loop, a call), NoVA
// on the screen, App Kit, one brain (scopes, how it knew, the verdicts),
// then the measured numbers. The numbers are written
// out under the player so they are quotable without it. Sources:
// media/results-film/measurements.json. Platform and App Kit stay two
// products.
const RESULTS = [
  {
    n: '0 · 0 · 1',
    k: 'Zero writes by the model',
    v: 'The model holds no pen. A write is a capability you declared on the workflow; the model proposes a payload, a person or a keyword filter says yes, and the runtime posts with its own credentials and writes one receipt per commit. If you did not allow the write, the call does not exist.',
    to: '/gate',
    cta: 'What a gate actually is',
  },
  {
    n: '12×',
    k: 'Same workflow, two bills',
    v: 'The product-launch swarm ran twice on the demo org today: routed to small models it cost $0.017 per run; one large model pinned on every step cost $0.198. Same gates, same output. Per 1,000 runs that is $16.55 against $198.25 (ledger deltas, list prices). The published 30–50× is against a large model calling tools in a loop on the same document-intake workflow — validated 25–115× across workflow mixes, we quote the conservative end — and it holds because reliability lives in the runtime, so smaller models do the steps.',
    to: '/gate#routing',
    cta: 'How a step picks its model',
  },
  {
    n: '56–68%',
    k: 'Dev Console routing bench',
    v: 'Thirty real requests replayed as live sessions, baseline frontier model vs routed, on nventr\u2019s lab instance (2026-10-08). On the 24 routable requests: 56% cheaper on the cloud runtime, 68% on the sovereign runner, with no pass regression (22/24 → 22/24, 19/24 → 21/24). Six frontier tasks are most of the bill and route to the baseline\u2019s own model, so the all-in headline is 21–23%.',
    to: '/platform/dev-console',
    cta: 'Dev Console',
  },
  {
    n: '35.1 s',
    k: 'A warm claim became an instance',
    v: 'App Kit is the dedicated instance. A warm-pool claim on nventr\u2019s fleet turned into your URL, database and layer repo, seeded, in 35.1 seconds (layer repo 3.7 s · host 4.0 s · usage key 4.4 s · re-pointed 28.4 s · seeded 35.1 s), then verified itself live: SPA, API, realtime and a two-turn Dev Console smoke. Asking Dev Console for a change produced a branch, an edit and a pull request in 25.1 seconds; releases ship behind a health check.',
    to: '/app-kit',
    cta: 'App Kit',
  },
  {
    n: '6.3 s',
    k: 'Acts on its own',
    v: 'On the lab instance a service reported down twice in a row; the fleet restarted it in 6.3 seconds. Nobody was paged, and the action is on the record (instance.selfHeal in the audit trail). A production deploy that fails its checks inside 30 minutes rolls back to the previous build, once.',
    to: '/app-kit#assurance',
    cta: 'Monitoring and assurance',
  },
];

export default function Watch() {
  const on = filmEnabled();
  const training = moduleBySlug('training-center');
  const [params, setParams] = useSearchParams();
  const [cut, setCutState] = useState(params.get('cut') === 'executive' ? 'executive' : 'watch');
  const setCut = (next) => {
    setCutState(next);
    const p = new URLSearchParams(params);
    if (next === 'executive') p.set('cut', 'executive');
    else p.delete('cut');
    setParams(p, { replace: true });
  };
  return (
    <PageWorld world={WATCH}>
    <div className="cs-store cs-watch">
      <header className="cs-store-hero">
        <h1>The bridge, and one brain</h1>
        <p style={{ maxWidth: 720 }}>
          {FILM_LENGTH} with sound. nventr is the bridge to the systems you
          use and the apps you build. You draw the work in nQube, a swarm
          plans it, the gate holds, and NoVA is on the screen with you. App
          Kit is the instance you design, preview, and ship. What you
          connect and what you build stay in one brain.
          Then the numbers, each measured on nventr&rsquo;s own fleet with
          the source on screen.
        </p>
      </header>

      <section className="cs-store-section cs-watch-stage">
        {on ? (
          <FilmPlayer cut={cut} onCut={setCut} />
        ) : (
          <p className="cs-store-note">The film is being re-cut. The results are below.</p>
        )}
      </section>

      <section className="cs-store-section" id="bridge">
        <h2>Chapter 1 · {CHAPTERS[0].title}</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          A company is the systems it uses and the applications it builds.
          You name the system. Credentials stay in the secret store, never
          in the model, and never on this page.{' '}
          <Link to="/platform">The platform →</Link>
        </p>
      </section>

      <section className="cs-store-section" id="platform">
        <h2>Chapter 2 · {CHAPTERS[1].title}</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          You draw the work in nQube: branches, loops, a step that calls
          another pipeline or itself. The canvas is the specification. On
          the launch swarm, specialists plan it together and share notes. A
          step starts on a small model, and you can bring your own keys.
          What you connected is what the next answer comes from.{' '}
          <Link to="/platform/nqube">nQube →</Link>
        </p>
      </section>

      <section className="cs-store-section" id="govern">
        <h2>Chapter 3 · {CHAPTERS[2].title}</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          One invoice, once. Anything over policy waits. A person says yes.
          The runtime posts, and the receipt stays. The same gate sits on a
          branch, a loop, or a call, and production requires a person. The
          run on screen is the demo organization&rsquo;s gated invoice.{' '}
          <Link to="/gate">What a gate is →</Link>
        </p>
      </section>

      <section className="cs-store-section" id="nova">
        <h2>Chapter 4 · {CHAPTERS[3].title}</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          NoVA is on the screen with you. It reads a redacted outline of
          the page, opens the module, and presses what your seat may. What
          it may not press, it points to: that button is yours.{' '}
          <Link to="/platform/nventr-agent">NoVA →</Link>
        </p>
      </section>

      <section className="cs-store-section" id="kit">
        <h2>Chapter 5 · {CHAPTERS[4].title}</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          App Kit is the instance. You design the app, preview it, and read
          the diff before a change ships. Then it is live: its address, its
          health, and a fleet that restarts a down service.{' '}
          <Link to="/app-kit">App Kit →</Link>
        </p>
      </section>

      <section className="cs-store-section" id="brain">
        <h2>Chapter 6 · {CHAPTERS[5].title}</h2>
        <p className="cs-store-note" style={{ maxWidth: 860 }}>
          One brain, four scopes: the platform, the organization, one app,
          or the instance you host. Every answer shows how it knew, and
          three verdicts say the brain is alive: populated, updating,
          current.{' '}
          <Link to="/platform/athena">Knowledge →</Link>
        </p>
      </section>

      <section className="cs-store-section" id="results">
        <h2>Chapter 7 · {CHAPTERS[6].title}</h2>
        <div className="cs-watch-results">
          {RESULTS.map((r) => (
            <article key={r.k} className="cs-sw-card">
              <div className="cs-watch-num">{r.n}</div>
              <h3>{r.k}</h3>
              <p>{r.v}</p>
              <Link to={r.to} className="cs-land-proof-link">{r.cta} →</Link>
            </article>
          ))}
        </div>
        <p className="cs-land-micro">
          Measured 2026-10-08 → 2026-10-10 on nventr&rsquo;s demo org and
          lab fleet, not a customer. Model prices are the providers&rsquo;
          list prices. The method behind each number is in the repo
          (<code>media/results-film/measurements.json</code>).
        </p>
      </section>

      {training && pageFilmsEnabled() && (
        <section className="cs-store-section cs-watch-stage" id="training">
          <h2>Second film · {training.name}</h2>
          <p className="cs-store-note" style={{ maxWidth: 860 }}>
            Training a model is the most expensive thing a workflow can do.
            A real run on the demo organization: six stages, a pre-flight
            of fifty-four checks that proves the maths and prices the run,
            a person approving the GPU hours, the loss curve, the held-out
            result. On a different afternoon the same panel says NO-GO and
            the GPU is never touched.{' '}
            <Link to="/platform/training-center">The Training Center →</Link>
          </p>
          <PageFilm base={training.film.base} prefix={training.film.prefix} title={training.film.title} summary={training.film.summary} chapters={training.film.chapters || []} />
        </section>
      )}

      <section className="cs-store-section" id="start">
        <h2>Start with one</h2>
        <div className="cs-land-price-grid cs-land-offer">
          <div className="cs-sw-card cs-land-featured">
            <h3>{platformStart.product}</h3>
            <div className="cs-sw-price">{platformStart.price}</div>
            <small>{platformStart.helper}</small>
            <div className="cs-land-cta-row">
              <Link to={platformStart.href} className="cs-btn cs-btn-primary">{platformStart.cta}</Link>
            </div>
          </div>
          <div className="cs-sw-card">
            <h3>{appKit.product}</h3>
            <div className="cs-sw-price">{appKit.price}</div>
            <small>{appKit.helper}</small>
            <div className="cs-land-cta-row">
              <Link to={appKit.href} className="cs-btn cs-btn-primary">{appKit.cta}</Link>
            </div>
          </div>
        </div>
        <p className="cs-land-micro">
          <Link to="/pricing">Full pricing →</Link>
        </p>
      </section>
    </div>
    </PageWorld>
  );
}
